import { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scannedCount: number;
  failedCount: number;
  totalCount: number;
  submitting?: boolean;
  mode: 'receiving' | 'dispatch';
  onSubmit: (driverAcknowledged: boolean, driverName: string) => void | Promise<void>;
}

export function CloseSessionDialog({
  open,
  onOpenChange,
  scannedCount,
  failedCount,
  totalCount,
  submitting,
  mode,
  onSubmit,
}: Props) {
  const [driverAcknowledged, setDriverAcknowledged] = useState(false);
  const [driverName, setDriverName] = useState('');

  const reset = () => {
    setDriverAcknowledged(false);
    setDriverName('');
  };

  const handleSubmit = async () => {
    if (!driverName.trim()) return;
    await onSubmit(driverAcknowledged, driverName.trim());
    reset();
  };

  const title = mode === 'receiving' ? 'إغلاق جلسة الاستلام' : 'إغلاق جلسة التسليم';
  const ackVerb = mode === 'receiving' ? 'استلام' : 'تسليم';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border rounded p-2">
              <p className="text-xs text-muted-foreground">المجموع</p>
              <p className="text-xl font-bold">{totalCount}</p>
            </div>
            <div className="border rounded p-2 bg-green-50">
              <p className="text-xs text-muted-foreground">تم {ackVerb}</p>
              <p className="text-xl font-bold text-green-700">{scannedCount}</p>
            </div>
            <div className="border rounded p-2 bg-red-50">
              <p className="text-xs text-muted-foreground">تباينات</p>
              <p className="text-xl font-bold text-red-700">{failedCount}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>اسم السائق *</Label>
            <Input
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              placeholder="اسم السائق الذي وقع على {ackVerb}"
              disabled={submitting}
            />
          </div>

          <div className="flex items-start gap-2 border rounded-lg p-3">
            <Checkbox
              id="driver_ack"
              checked={driverAcknowledged}
              onCheckedChange={(c) => setDriverAcknowledged(Boolean(c))}
              disabled={submitting}
            />
            <Label htmlFor="driver_ack" className="text-sm leading-snug cursor-pointer">
              السائق أقرّ بنتيجة الجلسة (التباينات إن وُجدت)
            </Label>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            إلغاء
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !driverName.trim()}>
            {submitting ? 'جارٍ الإغلاق...' : 'تأكيد الإغلاق'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
